import {
  AccordionSummary,
  Box,
  Paper,
  Stack,
  Accordion,
  Typography,
  useMediaQuery,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import theme from "../../../muiTheme";
import { formatStringDate } from "../../../utils/dateUtils/formatDate.util";
import { FormattedManagementsList } from "../../../core/models/managements/managements.model";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import {
  getIncidenceIconTitle,
  incedenceTexts,
} from "../../../utils/components/managements/managements.util";
import { SuppliesDetailHistoricColumns } from "../../../core/models/suppliesDetail/suppliesDetail.model";
import { getTextCodeText } from "../../../utils/components/supplies/supplies.util";
import "./SuppliesDetailHistoric.scss";
import ManagementsDetail from "../../Managements/ManagementsDetail/ManagementsDetail";
import SuppliesDetailHistoricManagements from "./SuppliesDetailHistoricManagements/SuppliesDetailHistoricManagements";
import { useState } from "react";

interface SuppliesDetailHistoricProps {
  codeCups: string;
  historicUpdates: FormattedManagementsList[];
  isLoading: boolean;
}

const SuppliesDetailHistoric = ({
  codeCups,
  historicUpdates,
  isLoading,
}: SuppliesDetailHistoricProps) => {
  const { t } = useTranslation();
  const isDesktopViewport = useMediaQuery("(min-width:744px)");
  const [managementSelected, setManagementSelected] = useState<string>("");
  const suppliesDetailHistoricColumns: SuppliesDetailHistoricColumns[] = [
    {
      id: "modificationType",
      label: "Tipo de modificación",
      width: "80%",
      paddingBottom: "12px",
      formatContent: ({
        recordTextType,
        claimTextSubType,
        incidenceText,
      }: FormattedManagementsList) => {
        const isIncidenceText = incedenceTexts.some((incidenceMatchingText) =>
          incidenceText.includes(incidenceMatchingText)
        );
        const incidenceIconTitle = getIncidenceIconTitle(
          incedenceTexts,
          incidenceText
        );
        return isIncidenceText
          ? incidenceIconTitle.title
          : getTextCodeText(recordTextType, claimTextSubType);
      },
    },
    {
      id: "date",
      label: "Fecha",
      width: "20%",
      paddingTop: "12px",
      formatContent: ({ date }: FormattedManagementsList) =>
        formatStringDate(date),
    },
  ];

  return (
    <Paper
      sx={{
        width: "100%",
        borderRadius: "12px",
        padding: "1.5rem",
        boxSizing: "border-box",
      }}
    >
      <Stack
        component={"div"}
        marginBottom={"1rem"}
        gap={{ xs: ".5rem", md: "1rem" }}
      >
        <Typography component="h3" variant="h3">
          {t("Histórico de modificaciones")}
        </Typography>
        <Typography
          component="h4"
          variant="subtitle1"
          color={theme.palette.grey[500]}
        >
          {`CUPS ${codeCups}`}
        </Typography>
      </Stack>
      {isLoading && (
        <Box>
          <LoadingComponentComponent
            isLoading={isLoading}
            position={"relative"}
            height={"300px"}
          />
        </Box>
      )}
      {!!historicUpdates.length && (
        <>
          {isDesktopViewport ? (
            <Box>
              <Stack
                display={"flex"}
                flexDirection={"row"}
                borderBottom={`1px solid ${theme.palette.grey[300]}`}
                marginTop={"1rem"}
                marginBottom={"2.2rem"}
              >
                {suppliesDetailHistoricColumns.map(({ id, label, width }) => (
                  <Typography
                    key={id}
                    width={width}
                    fontWeight={600}
                    paddingBottom={"4px"}
                    color={"#405258"}
                  >
                    {t(label)}
                  </Typography>
                ))}
                <Box component={"span"} width={"1.5rem"} />
              </Stack>
              <Stack gap={"1rem"}>
                {historicUpdates.map((historicUpdate) => {
                  const isManagementSelected =
                    managementSelected === historicUpdate.issueNumber;
                  return (
                    <Accordion
                      key={historicUpdate.issueNumber}
                      expanded={isManagementSelected}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon color="primary" />}
                        onClick={() =>
                          setManagementSelected(
                            isManagementSelected
                              ? ""
                              : historicUpdate.issueNumber
                          )
                        }
                      >
                        {suppliesDetailHistoricColumns.map(
                          ({ id, formatContent, width }) => (
                            <Typography key={id} width={width}>
                              {formatContent(historicUpdate)}
                            </Typography>
                          )
                        )}
                      </AccordionSummary>
                      <AccordionDetails>
                        <SuppliesDetailHistoricManagements
                          idIssue={
                            isManagementSelected ? +managementSelected : 0
                          }
                          recordTextType={historicUpdate.recordTextType}
                          claimTextSubType={historicUpdate.claimTextSubType}
                          incidenceText={historicUpdate.incidenceText}
                        />
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </Box>
          ) : (
            <Box>
              {historicUpdates.map((historicUpdate, index) => {
                const isLastElement = historicUpdates.length - 1 === index;
                const isManagementSelected =
                  managementSelected === historicUpdate.issueNumber;
                return (
                  <Accordion
                    key={historicUpdate.issueNumber}
                    expanded={isManagementSelected}
                    sx={{
                      borderBottom: !isLastElement
                        ? `1px solid ${theme.palette.grey[300]}`
                        : "",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon color="primary" />}
                      onClick={() =>
                        setManagementSelected(
                          isManagementSelected ? "" : historicUpdate.issueNumber
                        )
                      }
                    >
                      <Stack
                        display={"flex"}
                        gap={"4px"}
                        flexDirection={"column-reverse"}
                      >
                        {suppliesDetailHistoricColumns.map(
                          ({
                            id,
                            formatContent,
                            paddingBottom,
                            paddingTop,
                          }) => (
                            <Typography
                              key={id}
                              component={"p"}
                              variant={id === "date" ? "caption" : "body2"}
                              paddingBottom={
                                !isLastElement ? paddingBottom : undefined
                              }
                              paddingTop={index && paddingTop}
                            >
                              {formatContent(historicUpdate)}
                            </Typography>
                          )
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <SuppliesDetailHistoricManagements
                        idIssue={isManagementSelected ? +managementSelected : 0}
                        recordTextType={historicUpdate.recordTextType}
                        claimTextSubType={historicUpdate.claimTextSubType}
                        incidenceText={historicUpdate.incidenceText}
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default SuppliesDetailHistoric;
